// src/admin/AdminSideBar.js
import React, { useContext, useEffect, useState } from "react";
import {
  FaChartBar,
  FaBox,
  FaShoppingCart,
FaStar,
  FaUsers,
  FaEnvelopeOpenText,
  FaSignOutAlt,
  FaChartLine,
  FaStore,
  FaBell,
  FaExclamationTriangle,
  FaEnvelope,
  FaSearch,
  FaClock,
  FaImage,
  FaTimes,
  FaTruck,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { AdminLanguageContext } from "./context/AdminLanguageContext";
import { getUnreadNotificationsCount } from "../services/adminNotifications.api";
import { getPendingOrdersCount } from "../services/adminOrders.api";
import { getAdminMessages } from "../services/adminMessages.api";
import { getActiveCouponsCount } from "../services/adminCoupons.api";

function AdminSidebar({ isOpen, setIsOpen }) {
  const navigate = useNavigate();
  const { lang } = useContext(AdminLanguageContext);
  
  const [unreadCount, setUnreadCount] = useState(null);
  const [pendingOrders, setPendingOrders] = useState(null);
  const [openMessages, setOpenMessages] = useState(null);
  const [activeCoupons, setActiveCoupons] = useState(null);


  const adminUser = JSON.parse(localStorage.getItem("adminUser") || "{}");
  const perms = JSON.parse(localStorage.getItem("staffPermissions") || "{}");
  const isAdmin = adminUser.role === "admin";

  const [openDropdown, setOpenDropdown] = useState(null);

const toggleDropdown = (key) => {
  setOpenDropdown((prev) => (prev === key ? null : key));
};


  const TEXT = {
    EN: {
      panel: "Admin Panel",
      dashboard: "Dashboard",
      products: "Products",
      orders: "Orders",
      users: "Users",
      customers: "Customers",
      analytics: "Analytics",
      notifications: "Notifications",
      inventory: "Inventory Alerts",
      messages: "Messages",
      email: "Email Templates",
      settings: "Settings",
      seo: "SEO Manager",
      activity: "Top Banner",
      banners: "Promo Banners",
      logout: "Logout",
reviews: "Reviews",
      categories: "Categories",
userActivity: "User Activity",
promoCodes: "Promo Codes",
shipping: "Shipping",
abandonedCarts: "Abandoned Carts",
newIn: "New In",
bestsellers: "Bestsellers",
onSale: "On Sale",
emailTemplatesManager: "Email Templates",
    },
    AR: {
      panel: "لوحة الإدارة",
      dashboard: "لوحة التحكم",
      products: "المنتجات",
      orders: "الطلبات",
      users: "المستخدمين",
      customers: "العملاء",
      analytics: "التحليلات",
      notifications: "الإشعارات",
      inventory: "تنبيهات المخزون",
      messages: "الرسائل",
      email: "قوالب البريد",
      settings: "الإعدادات",
      seo: "إدارة SEO",
      activity: "Top Banner",
      banners: "العروض الترويجية",
      logout: "تسجيل الخروج",
      categories: "التصنيفات",
userActivity: "نشاط المستخدمين",
promoCodes: "أكواد الخصم",
shipping: "الشحن",
reviews: "المراجعات",
abandonedCarts: "السلال المتروكة",
newIn: "الجديد",
bestsellers: "الأكثر مبيعًا",
onSale: "التخفيضات",
emailTemplatesManager: "قوالب محتوى البريد",
    },
  }[lang];

const canProducts = !!(isAdmin || perms.can_manage_products);
const canOrders = !!(isAdmin || perms.can_manage_orders);
const canCustomers = !!(isAdmin || perms.can_manage_customers);
const canStaff = !!(isAdmin || perms.can_manage_staff);
const canMessages = !!(isAdmin || perms.can_manage_messages);
const canMarketing = !!(isAdmin || perms.can_manage_marketing);
const canAnalytics = !!(isAdmin || perms.can_view_analytics);
const canSEO = !!(isAdmin || perms.can_manage_seo);
const canBanners = !!(isAdmin || perms.can_manage_banners);
const canShipping = !!(isAdmin || perms.can_manage_shipping);



 useEffect(() => {
  // RESET ALL COUNTS FIRST ✅
  setUnreadCount(null);
setOpenMessages(null);
setPendingOrders(null);
setActiveCoupons(null);

  if (canMessages) {
    getUnreadNotificationsCount()
      .then((res) => setUnreadCount(res.count || 0))
      .catch(() => {});

    getAdminMessages()
      .then((list) => {
        const count = list.filter(
          (m) => m.status === "new" || m.status === "in_progress"
        ).length;
        setOpenMessages(count);
      })
      .catch(() => {});
  }

  if (canOrders) {
    getPendingOrdersCount()
      .then((res) => setPendingOrders(res.count || 0))
      .catch(() => {});
  }

  if (canMarketing) {
    getActiveCouponsCount()
      .then((res) => setActiveCoupons(res.count || 0))
      .catch(() => {});
  }
}, [canMessages, canOrders, canMarketing]);


const hasAnyPermission =
  canProducts ||
  canOrders ||
  canCustomers ||
  canStaff ||
  canMessages ||
  canMarketing ||
  canAnalytics ||
  canSEO ||
  canBanners ||
  canShipping;

const DropdownHeader = ({ icon, label, id }) => (
  <button
    onClick={() => toggleDropdown(id)}
    className="side-btn flex justify-between items-center"
  >
    <span className="flex items-center gap-3">
      {icon} {label}
    </span>
    <span className="text-sm">{openDropdown === id ? "▲" : "▼"}</span>
  </button>
);


  return (
    <>
      {/* MOBILE OVERLAY */}
      <div
        className={`fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden transition-opacity duration-300 ${
          isOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
        onClick={() => setIsOpen(false)}
      />

      {/* SIDEBAR PANEL */}
      <div
        className={`
          fixed top-0 left-0 h-screen w-64 bg-gray-900 text-white 
          z-50 flex flex-col shadow-xl
          transform transition-transform duration-300
          ${isOpen ? "translate-x-0" : "-translate-x-full"}
          lg:translate-x-0
        `}
      >
        {/* HEADER */}
        <div className="p-5 pb-3 border-b border-gray-700 flex justify-between items-center">
          <h1 className="text-2xl font-bold text-cyan-400">{TEXT.panel}</h1>

          <button
            className="lg:hidden text-white text-xl"
            onClick={() => setIsOpen(false)}
          >
            <FaTimes />
          </button>
        </div>

        {/* MENU */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-2">
 {!hasAnyPermission ? (
    <div className="text-gray-400 text-sm text-center mt-10">
      {lang === "AR"
        ? "لا توجد صلاحيات متاحة"
        : "No permissions assigned"}
    </div>
  ) : (
    <>
          {canAnalytics && (
  <button className="side-btn" onClick={() => navigate("/admin/dashboard")}>
    <FaChartBar /> {TEXT.dashboard}
  </button>
)}


         {canProducts && (
  <>
    <DropdownHeader
      id="products"
      icon={<FaBox />}
      label={TEXT.products}
    />

    {openDropdown === "products" && (
      <div className="ml-4 space-y-1">
        <button className="side-btn" onClick={() => navigate("/admin/products")}>
          {TEXT.products}
        </button>
        <button className="side-btn" onClick={() => navigate("/admin/categories")}>
          {TEXT.categories}
        </button>
        <button className="side-btn" onClick={() => navigate("/admin/products/new-in")}>
          {TEXT.newIn}
        </button>
        <button className="side-btn" onClick={() => navigate("/admin/products/bestsellers")}>
          {TEXT.bestsellers}
        </button>
        <button className="side-btn" onClick={() => navigate("/admin/products/on-sale")}>
          {TEXT.onSale}
        </button>
      </div>
    )}
  </>
)}



{canOrders && (
  <>
    <DropdownHeader
      id="orders"
      icon={<FaShoppingCart />}
      label={TEXT.orders}
    />

    {openDropdown === "orders" && (
      <div className="ml-4 space-y-1">
        <button className="side-btn" onClick={() => navigate("/admin/orders")}>
          {TEXT.orders}
          {pendingOrders > 0 && (
            <span className="ml-2 bg-red-500 text-xs px-2 rounded-full">
              {pendingOrders}
            </span>
          )}
        </button>

<button className="side-btn" onClick={() => navigate("/admin/reviews")}>
  {TEXT.reviews}
</button>

        <button className="side-btn" onClick={() => navigate("/admin/email-templates")}>
          {TEXT.promoCodes}
        </button>

        <button className="side-btn" onClick={() => navigate("/admin/shipping")}>
          {TEXT.shipping}
        </button>
      </div>
    )}
  </>
)}

{(canStaff || canCustomers) && (
  <>
    <DropdownHeader
      id="users"
      icon={<FaUsers />}
      label={TEXT.users}
    />

    {openDropdown === "users" && (
      <div className="ml-4 space-y-1">
        {canStaff && (
          <button className="side-btn" onClick={() => navigate("/admin/users")}>
            {TEXT.users}
          </button>
        )}
        {canCustomers && (
          <button className="side-btn" onClick={() => navigate("/admin/customers")}>
            {TEXT.customers}
          </button>
        )}
      </div>
    )}
  </>
)}

{canAnalytics && (
  <>
    <DropdownHeader
      id="analytics"
      icon={<FaChartLine />}
      label={TEXT.analytics}
    />

    {openDropdown === "analytics" && (
      <div className="ml-4 space-y-1">
        <button className="side-btn" onClick={() => navigate("/admin/analytics")}>
          {TEXT.analytics}
        </button>
        <button className="side-btn" onClick={() => navigate("/admin/users-activity")}>
          {TEXT.userActivity}
        </button>
        <button className="side-btn" onClick={() => navigate("/admin/abandoned-carts")}>
          {TEXT.abandonedCarts}
        </button>
      </div>
    )}
  </>
)}

{canMessages && (
  <>
    <DropdownHeader
      id="alerts"
      icon={<FaBell />}
      label={TEXT.notifications}
    />

    {openDropdown === "alerts" && (
      <div className="ml-4 space-y-1">
        <button className="side-btn" onClick={() => navigate("/admin/inventory")}>
          {TEXT.inventory}
        </button>
        <button className="side-btn" onClick={() => navigate("/admin/notifications")}>
          {TEXT.notifications}
        </button>
        <button className="side-btn" onClick={() => navigate("/admin/messages")}>
          {TEXT.messages}
        </button>
      </div>
    )}
  </>
)}



{(canMarketing || canSEO || canBanners) && (
  <>
    <DropdownHeader
      id="settings"
      icon={<FaStore />}
      label={TEXT.settings}
    />

    {openDropdown === "settings" && (
      <div className="ml-4 space-y-1">
        <button className="side-btn" onClick={() => navigate("/admin/email-template-manager")}>
          {TEXT.emailTemplatesManager}
        </button>
        <button className="side-btn" onClick={() => navigate("/admin/settings")}>
          {TEXT.settings}
        </button>
        <button className="side-btn" onClick={() => navigate("/admin/seo")}>
          {TEXT.seo}
        </button>
        <button className="side-btn" onClick={() => navigate("/admin/top-banner")}>
          {TEXT.activity}
        </button>
        <button className="side-btn" onClick={() => navigate("/admin/sale-banner")}>
          {TEXT.banners}
        </button>
      </div>
    )}
  </>
)}
</>
)}
</div>


        {/* LOGOUT */}
        <div className="p-5 border-t border-gray-700">
          <button
            className="flex items-center gap-3 p-3 rounded hover:bg-red-600 transition w-full"
            onClick={() => {
              localStorage.removeItem("adminToken");
localStorage.removeItem("adminUser");
localStorage.removeItem("staffPermissions");
navigate("/admin/login");
            }}
          >
            <FaSignOutAlt /> {TEXT.logout}
          </button>
        </div>
      </div>
    </>
  );
}

export default AdminSidebar;
